/**
 * Server-level instructions surfaced to MCP clients on connect.
 *
 * These are the primary signal for telling models *when* to reach for this
 * server. Keep the language imperative and the workflow steps explicit —
 * soft framing ("helpful to reference") gets ignored against stronger
 * signals from other MCPs.
 */

export const SERVER_INSTRUCTIONS = `This server provides authoritative design system documentation, components, patterns, and design tokens from zeroheight.

## When to use this server

Use this server BEFORE generating, modifying, or reviewing any of the following:
- UI components (buttons, inputs, modals, cards, layouts, navigation, etc.)
- Pages, screens, or views composed from design system primitives
- CSS, SCSS, Tailwind, or inline styles that touch color, spacing, typography, radius, shadow, or motion
- Theming, dark mode, or token-level changes
- Any code or markup the user describes as following a "design system", "style guide", "component library", or named system

If the user's request involves visual or structural UI work and a zeroheight styleguide is available, treat this server as the source of truth and consult it before writing code.

## Working alongside Storybook

zeroheight is the entry point, not a competitor to Storybook. \`get-page\` responses frequently embed Storybook URLs that point to the canonical implementation for a component (props, variants, runnable examples). The intended flow is:

1. Start here — call \`list-styleguides\` / \`list-pages\` / \`get-page\` first to find the authoritative documentation page for the component or pattern.
2. If the page links to Storybook, follow those URLs (or call a Storybook MCP if one is connected) to retrieve implementation-level details: exact prop names, types, valid combinations, and code examples.
3. Combine both: zeroheight provides the design intent, usage rules, and "when to use this"; Storybook provides the runtime API surface.

Do NOT skip step 1 and go straight to Storybook — the styleguide is what tells you *which* component to use and how it should behave. The Storybook URLs surface inside zeroheight pages for exactly this reason.

## Verification rules

- Never invent component names, prop names, variants, or valid prop combinations. Fetch the page that documents the component first.
- Never invent token names, values, or token set structure. Fetch the token set first.
- If a component, prop, pattern, or token is not documented in the styleguide, say so explicitly rather than guessing.
- After generating CSS/SCSS/TSX/TS/JS/JSX that uses design system styling, run \`lint-code\` against the relevant token set and apply the suggested fixes before returning code to the user.

## Documentation workflow

For UI / component / pattern work:
1. Call \`list-styleguides\` to discover available design systems (skip if only one is connected).
2. Call \`list-pages\` to see the navigation tree and locate the page documenting the component or pattern you need.
3. Call \`get-page\` to read the documentation. Treat its guidance, prop tables, variants, and usage notes as authoritative.
4. Call \`get-page-images\` (or fetch individual image resource URIs) only when visual examples are required for the task.

For token / styling work:
1. Call \`list-token-sets\` to discover available token sets.
2. Call \`get-tokens\` to retrieve the W3C token definitions for the relevant set.
3. Use the exact token names from the response — do not invent or approximate.
4. After writing styled code, call \`lint-code\` to catch hardcoded values that should be tokens, and apply the suggested replacements.

## Releases

All tools default to the latest published content. Only pass \`releaseId\` when the user has explicitly asked about a specific release. Page IDs differ between releases — if you use a \`releaseId\`, re-fetch the navigation tree with the same \`releaseId\` first.
`;
